import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Upload, X, ShieldCheck, MessageCircle, ArrowRight,
  Lock, Calculator, ChevronRight, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Listers Categories - OFFICIAL 8 CATEGORIES
const CATEGORIES = [
  'Home + Lifestyle',
  'Electronics + Devices',
  'Fashion + Luxury',
  'Health + Fitness',
  'Antiques + Collectibles',
  'Camping + Outdoors',
  'Auto Parts + Accessories',
  'Tools + Machinery'
];

const CONDITIONS = ['Poor', 'Fair', 'Good', 'As New', 'Brand New'];

const MAX_PHOTOS = 6;

export default function SalesApplicationForm() {
  const navigate = useNavigate();
  const location = useLocation();

  // Form analytics (starts vs submits)
  const sessionRef = React.useRef((window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`));
  const startedRef = React.useRef(false);
  const trackFormEvent = (event) => {
    axios.post(`${API}/analytics/form-event`, { event, session_id: sessionRef.current }).catch(() => {});
  };
  const markFormStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackFormEvent('start');
    }
  };

  // Item Details
  const [category, setCategory] = useState('');
  const [itemName, setItemName] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [year, setYear] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [condition, setCondition] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);

  // Seller Details
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [postcode, setPostcode] = useState('');
  const [facebookProfile, setFacebookProfile] = useState('');
  const [lowestPrice, setLowestPrice] = useState('');

  // Calculator
  const [calculatorPrice, setCalculatorPrice] = useState('');

  // Terms
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Pre-fill customer details if coming from success page
  // Report page height to parent when embedded in an iframe (avoids double scrollbars)
  useEffect(() => {
    if (window.parent === window) return;
    const send = () => window.parent.postMessage({ type: 'listers-form-height', height: document.documentElement.scrollHeight }, '*');
    send();
    const obs = new ResizeObserver(send);
    obs.observe(document.body);
    return () => obs.disconnect();
  }, []);


  useEffect(() => {
    if (location.state?.customerData) {
      const { firstName: prefillFirstName, surname: prefillSurname, email: prefillEmail, mobile: prefillMobile, postcode: prefillPostcode, facebook: prefillFacebook } = location.state.customerData;
      setFirstName(prefillFirstName || '');
      setSurname(prefillSurname || '');
      setEmail(prefillEmail || '');
      setMobile(prefillMobile || '');
      setPostcode(prefillPostcode || '');
      setFacebookProfile(prefillFacebook || '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.info('Your contact details have been pre-filled');
    }
  }, [location.state]);

  const calculatePayout = (salePrice) => {
    const price = parseFloat(salePrice) || 0;
    const sellerPayout = (price * 0.70).toFixed(2);
    const listersCommission = (price * 0.30).toFixed(2);
    return { sellerPayout, listersCommission };
  };

  const { sellerPayout, listersCommission } = calculatePayout(calculatorPrice);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, MAX_PHOTOS - photos.length);
    if (files.length === 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    Promise.all(
      files.map(file => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          const reader = new FileReader();

          reader.onload = (event) => {
            img.src = event.target.result;

            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const MAX_WIDTH = 1200;
              const MAX_HEIGHT = 1200;
              let width = img.width;
              let height = img.height;

              if (width > height) {
                if (width > MAX_WIDTH) {
                  height *= MAX_WIDTH / width;
                  width = MAX_WIDTH;
                }
              } else {
                if (height > MAX_HEIGHT) {
                  width *= MAX_HEIGHT / height;
                  height = MAX_HEIGHT;
                }
              }

              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(img, 0, 0, width, height);
              resolve(canvas.toDataURL('image/jpeg', 0.7));
            };

            img.onerror = reject;
          };

          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    ).then(base64Photos => {
      setPhotos([...photos, ...base64Photos]);
      toast.success(`${files.length} photo(s) added & compressed`);
    }).catch(() => {
      toast.error('Failed to upload photos');
    });
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const fail = (msg) => {
      setFormError(msg);
      toast.error(msg);
    };

    if (!category) {
      fail('Please select a category');
      return;
    }
    if (!itemName) {
      fail('Please enter what you are selling');
      return;
    }
    if (!condition) {
      fail('Please select the item condition');
      return;
    }
    if (!description.trim()) {
      fail('Please tell us about your item');
      return;
    }
    if (photos.length < 2) {
      fail('Please attach at least 2 photos of your item');
      return;
    }
    if (!quantity || parseInt(quantity) < 1) {
      fail('Please enter the quantity');
      return;
    }
    if (!firstName || !surname) {
      fail('Please enter your full name');
      return;
    }
    if (!mobile || !email) {
      fail('Please provide your contact details');
      return;
    }
    if (!postcode.trim()) {
      fail('Please enter your postcode');
      return;
    }
    if (!lowestPrice || parseFloat(lowestPrice) < 300) {
      fail('Minimum lowest accepted sale price is $300');
      return;
    }
    if (!agreedToTerms) {
      fail('You must read and agree to the terms and conditions');
      return;
    }

    setSubmitting(true);

    try {
      // Step 1: Create or find seller
      const sellerData = {
        full_name: `${firstName} ${surname}`,
        email: email,
        phone: mobile,
        postcode: postcode || null,
        facebook_profile: facebookProfile || null
      };

      // Public endpoint - no credentials so the form works when embedded on any site
      const sellerResponse = await axios.post(`${API}/sellers`, sellerData);
      const sellerId = sellerResponse.data.id;

      // Step 2: Create application with flat schema
      const applicationData = {
        seller_id: sellerId,
        item_title: itemName,
        category: category,
        description: `${condition} condition. ${description}${brand ? ` Brand: ${brand}.` : ''}${model ? ` Model: ${model}.` : ''}${color ? ` Color: ${color}.` : ''}${year ? ` Year: ${year}.` : ''}`,
        nominated_sale_price: parseFloat(lowestPrice),
        minimum_acceptable_price: parseFloat(lowestPrice),
        photos: photos
      };

      await axios.post(`${API}/applications`, applicationData);

      trackFormEvent('submit');

      navigate('/success', {
        state: {
          customerData: {
            firstName,
            surname,
            email,
            mobile,
            postcode,
            facebook: facebookProfile
          }
        }
      });

    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg || String(d)).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to submit application. Please check your details and try again.');
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "h-14 w-full rounded-lg border border-[#E2E4E0] bg-white px-4 text-base text-[#1E2117] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#C6E76C] focus:ring-2 focus:ring-[#C6E76C]/40 transition-colors";
  const labelClass = "block text-[13px] font-bold tracking-[0.06em] uppercase text-[#3D4237] mb-2";

  return (
    <div className="min-h-screen bg-[#F4F5F1]" style={{ fontFamily: 'Inter, system-ui, sans-serif' }} onFocusCapture={markFormStarted}>
      <style>{`
        .tilt { font-family: 'Tilt Warp', sans-serif; }
        .hero-grain {
          background-color: #0E1013;
          background-image: radial-gradient(ellipse 60% 50% at 85% 10%, rgba(198,231,108,0.06), transparent 60%),
                            radial-gradient(ellipse 40% 40% at 10% 90%, rgba(198,231,108,0.04), transparent 60%);
        }
        .step-line::before {
          content: '';
          position: absolute;
          left: 19px;
          top: 40px;
          bottom: -14px;
          width: 2px;
          background: linear-gradient(to bottom, rgba(198,231,108,0.45), rgba(198,231,108,0.08));
        }
        .step-line:last-child::before { display: none; }
      `}</style>

      {/* ============ DARK HERO ============ */}
      <div className="hero-grain text-white">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 pt-7 pb-12 lg:pb-16">
          {/* Top bar */}
          <div className="flex items-center justify-end mb-8">
            <div className="flex items-center gap-2 text-[13px] text-[#C9CDC2]">
              <ShieldCheck className="w-4 h-4 text-[#C6E76C]" />
              <span className="hidden sm:inline">Secure &amp; Confidential</span>
            </div>
          </div>

          <div className="max-w-[1180px] mx-auto">
            <div className="max-w-3xl">
              <h1 className="tilt text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-4" data-testid="form-headline">
                Got something<br />to <span className="text-[#C6E76C]">sell?</span>
              </h1>
              <p className="text-base md:text-lg text-[#B7BCAF] mb-8" data-testid="form-subtitle">
                Tell us about your item below — add a couple of photos and we'll take it from there.
              </p>
            </div>

            {/* What happens next — single row on desktop, stacked on mobile */}
            <div data-testid="what-happens-next">
              <h2 className="tilt text-lg md:text-xl font-bold text-[#C6E76C] mb-5">What happens next?</h2>
              {(() => {
                const steps = [
                  'Fill in the form with a few photos.',
                  'We review and reply by SMS in 3\u20135 days.',
                  'We collect, store and list your item.',
                  'We handle sales \u2014 you get paid when it sells.',
                ];
                const Dots = ({ vertical }) => (
                  <span
                    aria-hidden
                    className={
                      vertical
                        ? 'block w-px h-5 bg-[repeating-linear-gradient(to_bottom,#C6E76C_0_3px,transparent_3px_7px)]'
                        : 'hidden md:block flex-1 h-px bg-[repeating-linear-gradient(to_right,#C6E76C_0_4px,transparent_4px_9px)]'
                    }
                  />
                );
                return (
                  <ol className="flex flex-col md:flex-row md:items-center md:gap-2">
                    {steps.map((text, i) => (
                      <React.Fragment key={`step-${i}`}>
                        <li
                          className="flex md:flex-col items-center md:items-start gap-3 md:gap-2 md:flex-1"
                          data-testid={`step-${i + 1}`}
                        >
                          <div className="w-8 h-8 shrink-0 rounded-full bg-[#C6E76C] flex items-center justify-center font-black text-[#1E2117] text-sm">
                            {i + 1}
                          </div>
                          <p className="text-[13px] md:text-sm text-[#D7DBCF] leading-snug">{text}</p>
                        </li>
                        {i < steps.length - 1 && (
                          <div className="flex md:contents items-center my-1 md:my-0 ml-4 md:ml-0 gap-2">
                            <Dots vertical />
                            <ChevronDown className="md:hidden w-3.5 h-3.5 text-[#C6E76C]" />
                            <Dots />
                            <ChevronRight className="hidden md:block w-4 h-4 text-[#C6E76C] shrink-0" />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </ol>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* ============ SAFETY STRIP ============ */}
      <div className="bg-[#F0F4E4] border-b border-[#E3E8D5]">
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 py-6 flex items-start gap-4">
          <div className="w-12 h-12 shrink-0 rounded-full bg-[#E2ECC8] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#5A7014]" />
          </div>
          <div>
            <div className="font-bold text-[17px] text-[#1E2117] mb-0.5">Your info is safe with us.</div>
            <p className="text-[14px] text-[#5B6152] leading-relaxed max-w-3xl">
              <a href="privacy-policy.html" target="_blank" rel="noreferrer" className="font-semibold underline underline-offset-2 hover:text-[#5A7014]" data-testid="privacy-policy-link">Click here</a> to read our comprehensive privacy policy.
            </p>
          </div>
        </div>
      </div>

      {/* ============ FORM ============ */}
      <form onSubmit={handleSubmit}>
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-2 gap-8 items-start">

          {/* ---- Card 1: Item Details ---- */}
          <div className="bg-white rounded-2xl border border-[#E5E7E3] p-6 sm:p-8" data-testid="item-details-card">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-9 h-9 rounded-full bg-[#C6E76C] flex items-center justify-center font-black text-[#1E2117]">1</div>
              <h2 className="text-xl md:text-2xl font-bold text-[#1E2117]">Item Details</h2>
            </div>

            <div className="mb-6">
              <label className={labelClass}>Item Category <span className="text-[#E4572E]">*</span></label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className={`${inputClass} flex items-center justify-between`} data-testid="category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-[15px]">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-6">
              <label className={labelClass}>What are you selling? <span className="text-[#E4572E]">*</span></label>
              <Input value={itemName} onChange={(e) => setItemName(e.target.value)} placeholder="e.g. Vintage Wooden Dining Chair" className={inputClass} data-testid="item-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className={labelClass}>Brand</label>
                <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Nike, Samsung, IKEA" className={inputClass} data-testid="brand-input" />
              </div>
              <div>
                <label className={labelClass}>Model</label>
                <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Air Max, Galaxy S24" className={inputClass} data-testid="model-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className={labelClass}>Colour</label>
                <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Black, White, Blue" className={inputClass} data-testid="colour-input" />
              </div>
              <div>
                <label className={labelClass}>Year</label>
                <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2023" className={inputClass} data-testid="year-input" />
              </div>
            </div>

            <div className="mb-6">
              <label className={labelClass}>Quantity <span className="text-[#E4572E]">*</span></label>
              <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={inputClass} data-testid="quantity-input" />
              <p className="text-[12px] text-[#8A9080] mt-1.5">How many of this item are you selling?</p>
            </div>

            <div className="mb-6">
              <label className={labelClass}>Condition <span className="text-[#E4572E]">*</span></label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger className={`${inputClass} flex items-center justify-between`} data-testid="condition-select">
                  <SelectValue placeholder="Select condition..." />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CONDITIONS.map((c) => (
                    <SelectItem key={c} value={c} className="text-[15px]">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-6">
              <label className={labelClass}>Tell us about your item <span className="text-[#E4572E]">*</span></label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Age, original price, upgrades, faults, accessories, condition notes, or anything else useful..."
                rows={4}
                className={`${inputClass} h-auto py-3 resize-none`}
                data-testid="description-input"
              />
            </div>

            <div>
              <label className={labelClass}>Photos <span className="text-[#E4572E]">*</span> <span className="normal-case font-medium text-[#8A9080]">(at least 2 required)</span></label>
              <label
                htmlFor="photo-upload"
                className="block border-2 border-dashed border-[#D8DCD2] rounded-xl px-6 py-8 text-center cursor-pointer hover:border-[#C6E76C] hover:bg-[#FAFBF6] transition-colors"
                data-testid="photo-dropzone"
              >
                <Upload className="w-5 h-5 text-[#8A9080] mx-auto mb-2" />
                <div className="text-[14px] font-semibold text-[#3D4237]">Drag &amp; drop up to {MAX_PHOTOS} photos<br />or click to browse</div>
                <div className="text-[12px] text-[#9CA3AF] mt-2">Clear photos help us value your item accurately.<br />PNG, JPG or HEIC. Max 10MB each.</div>
              </label>
              <input id="photo-upload" type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {photos.map((photo, index) => (
                    <div key={`photo-${index}-${photo.slice(-12)}`} className="relative group">
                      <img src={photo} alt={`Item ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-[#E5E7E3]" />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-[#1E2117] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-photo-${index}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ---- Right column ---- */}
          <div className="space-y-8">
            {/* Card 2: Seller Details */}
            <div className="bg-white rounded-2xl border border-[#E5E7E3] p-6 sm:p-8" data-testid="seller-details-card">
              <div className="flex items-center gap-3 mb-7">
                <div className="w-9 h-9 rounded-full bg-[#C6E76C] flex items-center justify-center font-black text-[#1E2117]">2</div>
                <h2 className="text-xl md:text-2xl font-bold text-[#1E2117]">Seller Details</h2>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className={labelClass}>First Name <span className="text-[#E4572E]">*</span></label>
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" className={inputClass} data-testid="first-name-input" />
                </div>
                <div>
                  <label className={labelClass}>Surname <span className="text-[#E4572E]">*</span></label>
                  <Input value={surname} onChange={(e) => setSurname(e.target.value)} placeholder="Surname" className={inputClass} data-testid="surname-input" />
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Mobile <span className="text-[#E4572E]">*</span></label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-[14px] text-[#3D4237] pointer-events-none">
                    <span role="img" aria-label="Australia">&#127462;&#127482;</span>
                  </span>
                  <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="0412 345 678" className={`${inputClass} pl-12`} data-testid="mobile-input" />
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Email <span className="text-[#E4572E]">*</span></label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputClass} data-testid="email-input" />
              </div>

              <div className="mb-6">
                <label className={labelClass}>Postcode <span className="text-[#E4572E]">*</span></label>
                <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="6000" className={inputClass} data-testid="postcode-input" />
              </div>

              <div className="mb-6">
                <label className={labelClass}>Facebook Profile <span className="normal-case font-medium text-[#8A9080]">(Optional)</span></label>
                <Input value={facebookProfile} onChange={(e) => setFacebookProfile(e.target.value)} placeholder="Paste your Facebook profile link" className={inputClass} data-testid="facebook-input" />
              </div>

              <div>
                <label className={labelClass}>Lowest Accepted Sale Price <span className="text-[#E4572E]">*</span><br /><span className="normal-case font-medium text-[#8A9080]">(Minimum $300)</span></label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-[#3D4237] pointer-events-none">$</span>
                  <Input type="number" min="300" value={lowestPrice} onChange={(e) => { setLowestPrice(e.target.value); setCalculatorPrice(e.target.value); }} placeholder="300" className={`${inputClass} pl-9`} data-testid="lowest-price-input" />
                </div>
              </div>
            </div>

            {/* Card 3: Quick Payout Calculator */}
            <div className="bg-[#C6E76C] rounded-2xl p-6 sm:p-8" data-testid="payout-calculator-card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#1E2117] flex items-center justify-center font-black text-[#C6E76C]">3</div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#1E2117]">Quick Payout Calculator</h2>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#1E2117] flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-[#C6E76C]" />
                </div>
              </div>

              <label className="block text-[11px] font-bold tracking-[0.08em] uppercase text-[#3B4423] mb-2">Estimated Sale Price</label>
              <div className="relative mb-5">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[16px] font-semibold text-[#3D4237] pointer-events-none">$</span>
                <Input
                  type="number"
                  value={calculatorPrice}
                  onChange={(e) => setCalculatorPrice(e.target.value)}
                  placeholder="0.00"
                  className="h-12 w-full rounded-lg border-0 bg-white pl-9 pr-4 text-lg font-bold text-[#1E2117] focus:outline-none focus:ring-2 focus:ring-[#1E2117]/30"
                  data-testid="calculator-price-input"
                />
              </div>

              <div className="bg-[#1E2117] rounded-xl grid grid-cols-2 divide-x divide-[#33382A] overflow-hidden">
                <div className="px-4 py-4 text-center">
                  <div className="text-[12px] text-[#AEB5A3] mb-1">You receive (70%)</div>
                  <div className="text-xl sm:text-2xl font-black text-white" data-testid="calculator-payout">${sellerPayout}</div>
                </div>
                <div className="px-4 py-4 text-center">
                  <div className="text-[12px] text-[#AEB5A3] mb-1">Listers fee (30%)</div>
                  <div className="text-xl sm:text-2xl font-black text-white" data-testid="calculator-commission">${listersCommission}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Please note + terms ---- */}
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8">
          <div className="bg-[#F0F4E4] border border-[#E3E8D5] rounded-2xl p-5 sm:p-6 flex items-start gap-4">
            <div className="w-11 h-11 shrink-0 rounded-full bg-[#E2ECC8] flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#5A7014]" />
            </div>
            <div>
              <p className="text-[13px] text-[#5B6152] leading-relaxed">
                <strong className="text-[#1E2117]">Please note:</strong> Listers communicates with sellers primarily via SMS text message.
                By submitting this application, you consent to receiving transactional text messages from Listers regarding your item,
                including application status updates, appointment confirmations, and item-related inquiries. Standard messaging rates may apply.
              </p>
              <label className="flex items-center gap-3 mt-4 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-5 h-5 accent-[#5A7014] cursor-pointer"
                  data-testid="terms-checkbox"
                />
                <span className="text-[14px] text-[#1E2117]">
                  I have read and understood the{' '}
                  <button type="button" onClick={() => setShowTerms(true)} className="font-bold underline underline-offset-2 hover:text-[#5A7014]" data-testid="terms-link">
                    Terms and Conditions
                  </button>{' '}
                  of selling with Listers <span className="text-[#E4572E]">*</span>
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* ---- Bottom CTA bar ---- */}
        <div className="max-w-[1180px] mx-auto px-5 sm:px-8 py-8 pb-16">
          <div className="bg-[#111418] rounded-2xl px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-center gap-6 sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 rounded-full bg-[#1C2127] border border-[#282E36] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#C6E76C]" />
              </div>
              <div>
                <div className="tilt text-[18px] font-black text-white">Ready to get started?</div>
                <div className="text-[13px] text-[#9AA093]">Complete the application and we&apos;ll be in touch within 3&ndash;5 working days via SMS.</div>
              </div>
            </div>
            <div className="flex flex-col items-center sm:items-end gap-2 shrink-0">
              {formError && (
                <div className="max-w-xs text-[14px] font-semibold text-[#FF9C8B] text-center sm:text-right" data-testid="form-error-message" role="alert">
                  {formError}
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="h-12 px-8 rounded-xl bg-[#C6E76C] hover:bg-[#B5D95A] text-[#1E2117] text-[16px] font-black flex items-center gap-2 disabled:opacity-60"
                data-testid="submit-application-button"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
                {!submitting && <ArrowRight className="w-5 h-5" />}
              </Button>
              <div className="flex items-center gap-1.5 text-[12px] text-[#9AA093]">
                <Lock className="w-3.5 h-3.5" /> Secure submission
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Terms and Conditions Modal */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#2C2C2C] flex items-center gap-3">
              <div className="w-1 h-8 bg-[#C6E76C] rounded-full"></div>
              Terms and Conditions - Selling with Listers
            </DialogTitle>
            <DialogDescription className="sr-only">Listers selling terms and conditions</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm text-[#4B5563] leading-relaxed">
            <p><strong className="text-[#2C2C2C]">Submission does not guarantee acceptance.</strong> Submitting an item does not mean Listers has agreed to sell it. Listers may accept or decline any item at its discretion.</p>
            <p><strong className="text-[#2C2C2C]">Minimum item value.</strong> Items generally must have an estimated resale value of $300 or more. Items below this amount may not be accepted.</p>
            <p><strong className="text-[#2C2C2C]">Accurate information required.</strong> You confirm that all information provided about the item, including its brand, model, condition, age, faults, accessories and estimated value, is true and accurate to the best of your knowledge.</p>
            <p><strong className="text-[#2C2C2C]">You must own the item.</strong> You confirm that you are the legal owner of the item, or have full authority from the owner to sell it.</p>
            <p><strong className="text-[#2C2C2C]">No stolen, illegal or restricted goods.</strong> You must not submit stolen goods, counterfeit goods, prohibited goods, unsafe goods, recalled items, or anything you are not legally allowed to sell.</p>
            <p><strong className="text-[#2C2C2C]">Item inspection.</strong> Listers may inspect, test, photograph and assess the item before deciding whether to list it for sale.</p>
            <p><strong className="text-[#2C2C2C]">Final sale price approval.</strong> You may nominate your lowest accepted sale price. Listers will not sell the item below that amount without your approval.</p>
            <p><strong className="text-[#2C2C2C]">Listers commission.</strong> Listers receives 30% of the final sale price as commission. This amount is automatically deducted before your payout is issued.</p>
            <p><strong className="text-[#2C2C2C]">Seller payout.</strong> You receive 70% of the final sale price, paid to your nominated bank account after the item has sold and payment has cleared.</p>
            <p><strong className="text-[#2C2C2C]">Payout timing.</strong> Listers aims to issue seller payouts within 24 hours of sale completion, provided buyer payment has cleared and there are no disputes, chargebacks, refunds or issues with the transaction.</p>
            <p><strong className="text-[#2C2C2C]">No upfront listing fee.</strong> There are no upfront fees to submit an item for review. Listers only receives commission if the item sells.</p>
            <p><strong className="text-[#2C2C2C]">Photos and item details.</strong> You authorise Listers to use the photos, item information and descriptions you provide for the purpose of reviewing, listing, marketing and selling the item.</p>
            <p><strong className="text-[#2C2C2C]">Listing content.</strong> Listers may write, edit, photograph, price, advertise and manage the item listing in a way it considers appropriate to maximise the chance of sale.</p>
            <p><strong className="text-[#2C2C2C]">Buyer communication.</strong> Listers will manage buyer messages, enquiries, negotiations and meetups relating to the item once it has been accepted for sale.</p>
            <p><strong className="text-[#2C2C2C]">Storage and handling.</strong> If Listers takes possession of the item, you authorise Listers to store, move, photograph, clean, test and handle the item as reasonably required for resale.</p>
            <p><strong className="text-[#2C2C2C]">Item condition after submission.</strong> You must disclose any known damage, faults, missing parts or issues before Listers accepts the item. Listers is not responsible for undisclosed issues that affect the sale.</p>
            <p><strong className="text-[#2C2C2C]">Withdrawal of item.</strong> You may request to stop selling your item before it is sold. If the item has already been sold, the sale cannot be cancelled unless Listers agrees.</p>
            <p><strong className="text-[#2C2C2C]">Unsold items.</strong> If an item does not sell within a reasonable period, Listers may recommend a price adjustment, return the item, or stop attempting to sell it.</p>
            <p><strong className="text-[#2C2C2C]">Rejected items.</strong> If Listers decides not to accept or continue selling an item, you may be required to collect it within a reasonable timeframe.</p>
            <p><strong className="text-[#2C2C2C]">Buyer disputes or refunds.</strong> If a buyer dispute, refund, return, payment reversal or chargeback occurs, your payout may be delayed, reduced, withheld or adjusted until the issue is resolved.</p>
            <p><strong className="text-[#2C2C2C]">Response timeframe.</strong> Listers aims to respond to sales requests within 5 business days, but response times may vary depending on demand and the information provided.</p>
            <p><strong className="text-[#2C2C2C]">Privacy.</strong> Your personal information will only be used for assessing your submission, contacting you about your item, managing the sale, issuing payment, and providing Listers services.</p>
            <p><strong className="text-[#2C2C2C]">Right to refuse service.</strong> Listers may refuse any item, seller, transaction or request at its discretion.</p>
            <p><strong className="text-[#2C2C2C]">Agreement before proceeding.</strong> Listers is not required to collect, list, store or sell your item until it has formally accepted the item and confirmed the next steps with you.</p>
          </div>

          <div className="mt-6">
            <Button
              onClick={() => setShowTerms(false)}
              className="w-full bg-[#C6E76C] hover:bg-[#A8C958] text-[#2C2C2C] font-bold"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
